import './scenario-planner-details-page.component.scss';
import './scenario-planner-mass-edit.component.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Nav,
  OverlayTrigger,
  Popover,
  Dropdown,
} from 'react-bootstrap';
import { AppErrorBoundaryComponent } from '../../components/error-boundary/app-error-boundary.component';
import { FilterPaneHeaderStandAloneComponent } from '../../components/filters/components/filter-pane-header/filter-pane-header-stand-alone.component';
import { FilterTabButtonStandAloneComponent } from '../../components/filters/components/filter-tab-button/filter-tab-button-stand-alone.component';
import {
  hasFilterSelectedValue,
  useConcatenateFilterSelection,
} from '../../components/filters/hooks/common.hook';
import { FILTER_NAMES } from '../../components/filters/hooks/filter-names.constants';
import { useEncodeFilters } from '../../components/filters/hooks/use-encode-filters.hook';
import {
  useFilterContext,
  useFilterInitialFilterSelectionContext,
} from '../../components/filters/provider/filter.provider';
import { REPORT_NAMES } from '../../components/reports/constants/report-names.constants';
import { AppTabularColumn } from '../../components/reports/dtos/app-tabular.dto';
import { AppFilterHelper } from '../../hooks/filters/helpers/app-filter.helper';
import { FilterSelectedValue } from '../../hooks/filters/interfaces/filter-selected-value.interface';
import { FILTER_TYPES } from '../../hooks/filters/interfaces/filter.interface';
import { useAppAsyncFilterOptions } from '../../hooks/filters/use-app-filter-options.hook';
import {
  useAppFilterDict,
  useAppFindFilters,
} from '../../hooks/filters/use-app-filters.hook';
import {
  ScenarioPlannerExtended,
  ScenarioPlannerTimeframe,
} from '../../hooks/scenario-planner/scenario-planner.interface';
import { useScenarioPlanner } from '../../hooks/scenario-planner/use-scenario-planner.hook';
import {
  postScenarioPlannerInput,
  postScenarioPlannerInputMode,
} from '../../services/scenario-planner.service';
import { IMAGES_REPO } from '../../static-resources/home-image-repo';
import { formatPeriod } from '../../utils/formatting/date-formatter.helper';
import { BodySectionComponent } from '../layout/components/body-section/body-section.component';
import useSwitchPanel from '../layout/hooks/switch-panel.hook';
import CopyScenarioModal from './components/copy-scenario-modal/copy-scenario.modal';
import MassEditSidebarComponent, {
  ApplyTo,
  MassEditBlock,
  MassEditField,
} from './components/mass-edit-sidebar/mass-edit-sidebar.component';
import ScenarioHeaderComponent from './components/scenario-header/scenario-header.component';
import { ScenarioPlannerDetailsTableComponent } from './components/scenario-planner-details/scenario-planner-details-table.component';
import { ScenarioPlannerSummaryTableComponent } from './components/scenario-planner-details/scenario-planner-summary-table.component';
import { ScenarioPlannerEditModalComponent } from './components/scenario-planner-edit/scenario-planner-edit.modal';
import ScenarioLayoutManagerComponent from './scenario-layout-manager.component';

const matchStrategy = AppFilterHelper.getMatchStrategyMultiple();
const parentCategoryValue = {
  value: FILTER_NAMES.CATEGORY,
  label: 'Category',
};
const defaultProdHier: FilterSelectedValue = {
  id: FILTER_NAMES.SCENARIOPLANNERPRODHIER,
  values: [
    {
      value: FILTER_NAMES.MANUFACTURER,
      label: 'Manufacturer',
    },
  ],
};

interface ScenarioPlannerMassEditComponentProps {
  scenarioPlanner: ScenarioPlannerExtended;
  scenarioPlannerFilterSel: FilterSelectedValue[];
  timeframe: ScenarioPlannerTimeframe;
  id: string;
}

interface RowActionDropdownProps {
  selectedRows: string[];
  onHideSelected: () => void;
  onSelectRowsToHide: () => void;
  onShowSelected: () => void;
  onResetRows: () => void;
  hasSelection: boolean;
  hasHiddenRows: boolean;
  isShowSelectedMode: boolean;
}

const RowActionDropdown = ({
  selectedRows,
  onHideSelected,
  onSelectRowsToHide,
  onShowSelected,
  onResetRows,
  hasSelection,
  hasHiddenRows,
  isShowSelectedMode,
}: RowActionDropdownProps) => {
  return (
    <>
      <Button
        variant={hasSelection ? 'primary' : 'outline-secondary'}
        onClick={onHideSelected}
        disabled={!hasSelection}
        size="sm"
        className="btn-common btn btn-outline-secondary"
      >
        Hide Selected ({selectedRows.length})
      </Button>

      <Dropdown>
        <Dropdown.Toggle
          variant="outline-secondary"
          className="btn-common btn btn-outline-secondary"
          id="edit-rows-dropdown"
          size="sm"
        >
          Edit Rows
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={onSelectRowsToHide} disabled={!hasSelection}>
            Select Rows to Hide
          </Dropdown.Item>
          <Dropdown.Item onClick={onShowSelected} disabled={!hasSelection}>
            Show Selected
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={onResetRows}
            disabled={!hasHiddenRows && !isShowSelectedMode}
          >
            Reset Rows
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

const ScenarioPlannerMassEditComponent = ({
  scenarioPlanner,
  id,
  scenarioPlannerFilterSel,
  timeframe,
}: ScenarioPlannerMassEditComponentProps) => {
  const [marketText, setMarketText] = useState<string>('');
  const [categoryText, setCategoryText] = useState<string>('');
  const [periodText, setPeriodText] = useState<string>('');
  const [timeframeName, setTimeframeName] = useState<string>('');
  const [ppgSelValue, setPggDt] = useState<FilterSelectedValue>({
    id: FILTER_NAMES.PROD,
    values: [],
    matchStrategy,
  });
  const [prodHierSelValue, setProdSelHier] =
    useState<FilterSelectedValue>(defaultProdHier);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<string | null>(
    null,
  );
  const [filterSelectionForMassEdit, setFilterSelectionForMassEdit] = useState<
    FilterSelectedValue[]
  >(useFilterInitialFilterSelectionContext());

  // Column visibility states
  const [availableColumns, setAvailableColumns] = useState<AppTabularColumn[]>(
    [],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Row hiding states - use Set<string> for row IDs
  const [hiddenRowsByTab, setHiddenRowsByTab] = useState<{
    [tabKey: string]: Set<string>;
  }>({});
  const [selectedRowsForHiding, setSelectedRowsForHiding] = useState<string[]>(
    [],
  );
  const [showSelectedMode, setShowSelectedMode] = useState<string[]>([]);

  // Scenario Summary tab is selected
  const scenarioSumarySelected = useMemo(() => {
    if (prodHierSelValue.values.find((i) => i.value === 'scenarioSummary')) {
      return true;
    } else {
      return false;
    }
  }, [prodHierSelValue]);

  // Helper function to get the current tab key
  const getCurrentTabKey = useCallback(() => {
    if (scenarioSumarySelected) {
      return 'scenarioSummary';
    }
    return prodHierSelValue.values[0]?.value || 'default';
  }, [scenarioSumarySelected, prodHierSelValue]);

  // Helper functions for row hiding operations
  const getCurrentHiddenRows = useCallback(() => {
    const tabKey = getCurrentTabKey();
    return hiddenRowsByTab[tabKey] || new Set<string>();
  }, [hiddenRowsByTab, getCurrentTabKey]);

  const updateHiddenRowsForCurrentTab = useCallback(
    (updateFn: (currentSet: Set<string>) => Set<string>) => {
      const tabKey = getCurrentTabKey();
      setHiddenRowsByTab((prev) => ({
        ...prev,
        [tabKey]: updateFn(prev[tabKey] || new Set<string>()),
      }));
    },
    [getCurrentTabKey],
  );

  const handleRowSelectionForHiding = useCallback(
    (selectedRowIds: string[]) => {
      setSelectedRowsForHiding(selectedRowIds);
    },
    [],
  );

  const handleHideSelected = useCallback(() => {
    if (!selectedRowsForHiding || selectedRowsForHiding.length === 0) {
      return;
    }

    // Validate each row ID before adding to hidden set
    const validRowIds = selectedRowsForHiding.filter((rowId) => {
      const isValid =
        rowId && typeof rowId === 'string' && rowId.trim().length > 0;
      return isValid;
    });

    if (validRowIds.length === 0) {
      return;
    }

    updateHiddenRowsForCurrentTab((currentHidden) => {
      const newHidden = new Set(currentHidden);
      validRowIds.forEach((rowId) => {
        newHidden.add(rowId.trim());
      });
      return newHidden;
    });

    // Clear selection after hiding
    setSelectedRowsForHiding([]);
  }, [selectedRowsForHiding, updateHiddenRowsForCurrentTab]);

  const handleSelectRowsToHide = useCallback(() => {
    handleHideSelected();
  }, [handleHideSelected]);

  const handleShowSelectedInternal = useCallback((selectedRows: string[]) => {
    if (!selectedRows || selectedRows.length === 0) {
      return;
    }

    // Validate row IDs
    const validRowIds = selectedRows.filter((rowId) => {
      const isValid =
        rowId && typeof rowId === 'string' && rowId.trim().length > 0;

      return isValid;
    });

    if (validRowIds.length === 0) {
      return;
    }

    const uniqueRowIds = Array.from(
      new Set(validRowIds.map((id) => id.trim())),
    );
    setShowSelectedMode(uniqueRowIds);

    setSelectedRowsForHiding([]);
  }, []);

  const handleResetRows = useCallback(() => {
    updateHiddenRowsForCurrentTab(() => new Set<string>());

    setShowSelectedMode([]);
    setSelectedRowsForHiding([]);
  }, [updateHiddenRowsForCurrentTab]);

  const currentTabKey = useMemo(() => getCurrentTabKey(), [getCurrentTabKey]);

  useEffect(() => {
    setSelectedRowsForHiding([]);
    setShowSelectedMode([]);
  }, [currentTabKey]);

  // To always include Category, we combine category level along with the tab selected.
  const prodHierSelFilter = useMemo(() => {
    const values = scenarioSumarySelected
      ? prodHierSelValue.values // Only selected product level.
      : [parentCategoryValue].concat(prodHierSelValue.values); // Force category along with selected level.
    return { ...prodHierSelValue, values };
  }, [prodHierSelValue, scenarioSumarySelected]);
  // Combines filter selection of the Scenario with glogal filters. It is used to filter dropdown options.
  const filterSelection = useConcatenateFilterSelection(
    scenarioPlannerFilterSel,
    filterSelectionForMassEdit,
  );
  // Summary section should only use scenario filters + product hierarchy, NOT the mass edit filters from top section
  const filterSelectionForSummary = useConcatenateFilterSelection(
    scenarioPlannerFilterSel,
    [prodHierSelFilter],
  );
  const filterSelectionForExport = useConcatenateFilterSelection(
    filterSelection,
    [prodHierSelFilter],
  );
  const filterSelectionForSummaryTabs = [prodHierSelValue];

  const { data } = useScenarioPlanner(id);

  const [isMassEditOpen, setIsMassEditOpen] = useState(false);
  const [defaultApplyMode, setDefaultApplyMode] = useState<ApplyTo>('filtered');
  const [isResettingAll, setIsResettingAll] = useState(false);

  const handleMassEditOpen = useCallback(() => {
    setIsMassEditOpen(true);
  }, []);
  const handleMassEditClose = () => setIsMassEditOpen(false);

  const [dataTableRefresh, setDataTableRefresh] = useState<{
    invalidateQuery: () => Promise<void>;
  }>();
  const [calcsTableRefresh, setCalcsTableRefresh] = useState<{
    invalidateQuery: () => Promise<void>;
  }>();
  const handleRunUpdates = useCallback(() => {
    if (dataTableRefresh) {
      dataTableRefresh.invalidateQuery();
    }
    if (calcsTableRefresh) {
      calcsTableRefresh.invalidateQuery();
    }
  }, [dataTableRefresh, calcsTableRefresh]);

  const handleRunChangeMode = useCallback(
    async (massEditField: MassEditField[]) => {
      if (id) {
        await postScenarioPlannerInputMode<any>(id, massEditField);
        setIsMassEditOpen(false);
        handleRunUpdates();
      }
    },
    [id, handleRunUpdates],
  );

  const handleRunEdit = useCallback(
    async (
      massEditBlock: MassEditBlock[],
      filterSelection: FilterSelectedValue[],
      updateIfZero: boolean,
    ) => {
      if (id) {
        const filteSel = filterSelection.filter((i) =>
          hasFilterSelectedValue(i),
        );
        await postScenarioPlannerInput<any>(
          id,
          massEditBlock,
          filteSel,
          updateIfZero,
        );
        setIsMassEditOpen(false);
        handleRunUpdates();
      }
    },
    [id, handleRunUpdates],
  );

  // Callback to receive column data from summary table component
  const handleColumnsUpdate = useCallback(
    (columns: AppTabularColumn[]) => {
      setAvailableColumns(columns);
      // Initialize visible columns with all columns if not set
      if (visibleColumns.length === 0) {
        setVisibleColumns(columns.map((col) => col.id));
      }
    },
    [visibleColumns.length],
  );

  // Handle column visibility toggle
  const handleColumnVisibilityToggle = useCallback((columnId: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        // Don't allow hiding all columns
        if (prev.length > 1) {
          return prev.filter((id) => id !== columnId);
        }
        return prev;
      } else {
        return [...prev, columnId];
      }
    });
  }, []);

  // Handle select/deselect all columns
  const handleSelectAllColumns = useCallback(
    (selectAll: boolean) => {
      if (selectAll) {
        setVisibleColumns(availableColumns.map((col) => col.id));
      } else {
        // Keep at least one column visible
        if (availableColumns.length > 0) {
          setVisibleColumns([availableColumns[0].id]);
        }
      }
    },
    [availableColumns],
  );

  // When changing values in the main table, zero values can be overidden.
  const handleTableRunEdit = useCallback(
    async (
      massEditBlock: MassEditBlock[],
      filterSelection: FilterSelectedValue[],
    ) => {
      await handleRunEdit(massEditBlock, filterSelection, true);
    },
    [handleRunEdit],
  );
  // When changing values from Mass Edit panel, measures with zero must not updates.
  const handleRunMassEdit = useCallback(
    async (
      massEditBlock: MassEditBlock[],
      applyMode: ApplyTo,
      customSelection?: FilterSelectedValue[],
    ) => {
      switch (applyMode) {
        case 'all':
          await handleRunEdit(massEditBlock, [], false);
          break;
        case 'filtered':
          await handleRunEdit(massEditBlock, filterSelectionForMassEdit, false);
          break;
        case 'edited':
          await handleRunEdit(massEditBlock, [ppgSelValue], false);
          break;
        case 'custom':
          if (customSelection) {
            await handleRunEdit(massEditBlock, customSelection, false);
          }
          break;
        default:
          await handleRunEdit(massEditBlock, filterSelectionForMassEdit, false);
      }
    },
    [filterSelectionForMassEdit, ppgSelValue, handleRunEdit],
  );

  useEffect(() => {
    const selVal = hasFilterSelectedValue(ppgSelValue);
    if (selVal) {
      setDefaultApplyMode('edited');
    } else {
      setDefaultApplyMode('filtered');
    }
  }, [ppgSelValue]);
  useEffect(() => {
    if (scenarioPlanner) {
      if (scenarioPlanner.filterSelection) {
        const market = hasFilterSelectedValue(
          scenarioPlanner.filterSelection.find(
            (i) => i.id === FILTER_NAMES.MARKET,
          ),
        );
        if (market) {
          setMarketText(market.values[0].label);
        }
        const cat = hasFilterSelectedValue(
          scenarioPlanner.filterSelection.find(
            (i) => i.id === FILTER_NAMES.CATEGORY,
          ),
        );
        if (cat) {
          setCategoryText(cat.values.map((i) => i.label).join('|'));
        }
      }
    }
    if (timeframe) {
      setPeriodText(formatPeriod([timeframe.startDate, timeframe.endDate]));
      setTimeframeName(timeframe.timeframeName);
    }
  }, [scenarioPlanner, timeframe]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const thElements = document.querySelectorAll(
        '.scenario-planner-details-table th',
      );
      thElements.forEach((th) => {
        const text = th.textContent?.trim();
        if (text === 'Current') {
          th.classList.add('add-left-border');
        }
      });
    });

    const table = document.querySelector('.scenario-planner-details-table');
    if (table) {
      observer.observe(table, { childList: true, subtree: true });
    }

    // Initial call in case it's already rendered
    setTimeout(() => {
      const thElements = document.querySelectorAll(
        '.scenario-planner-details-table th',
      );
      thElements.forEach((th) => {
        const text = th.textContent?.trim();
        if (text === 'Current') {
          th.classList.add('add-left-border');
        }
      });
    }, 200);

    return () => {
      observer.disconnect();
    };
  }, [data]);

  const { options: inputTypeOptions } = useAppAsyncFilterOptions(
    FILTER_NAMES.SCENARIOPLANNERINPUTYPE,
    [],
    null,
  );
  // Gets available field metrics
  const { options: inputFields } = useAppAsyncFilterOptions(
    FILTER_NAMES.SCENARIOPLANNERFIELD,
    [],
    null,
  );
  const { options: mainElastFields } = useAppAsyncFilterOptions(
    FILTER_NAMES.SCENARIOPLANNERFIELDMAIN,
    [],
    null,
  );
  const { options: crossElastFields } = useAppAsyncFilterOptions(
    FILTER_NAMES.SCENARIOPLANNERFIELDCROSS,
    [],
    null,
  );
  const { options: startingMetricsFields } = useAppAsyncFilterOptions(
    FILTER_NAMES.SCENARIOPLANNERFIELDSTARTING,
    [],
    null,
  );

  const defaultEditBlocks: MassEditBlock[] | undefined = useMemo(() => {
    const option = inputFields[0];
    if (option) {
      return [{ fieldId: option.value, inputType: 'perc', value: undefined }];
    }
  }, [inputFields]);

  // Create edit blocks for all input fields
  const inputsBlocks: MassEditBlock[] = useMemo(
    () =>
      inputFields.map((field) => ({
        fieldId: field.value,
        inputType: 'default', // Default input type
        value: undefined, // Clear the value
      })),
    [inputFields],
  );
  // Create edit blocks for all main fields
  const mainElastBlocks: MassEditBlock[] = useMemo(
    () =>
      mainElastFields.map((field) => ({
        fieldId: field.value,
        inputType: 'abs', // Default input type
        value: undefined, // Clear the value
      })),
    [mainElastFields],
  );
  // Create edit blocks for all input fields
  const startingMetricsBlocks: MassEditBlock[] = useMemo(
    () =>
      startingMetricsFields.map((field) => ({
        fieldId: field.value,
        inputType: 'abs', // Default input type
        value: undefined, // Clear the value
      })),
    [startingMetricsFields],
  );
  // Create edit blocks for all input, main, and starting metric fields
  const editBlocks: MassEditBlock[] = useMemo(
    () => inputsBlocks.concat(mainElastBlocks).concat(startingMetricsBlocks),
    [inputsBlocks, mainElastBlocks, startingMetricsBlocks],
  );

  const crossElastBlocks: MassEditBlock[] = useMemo(
    () =>
      crossElastFields.map((field) => ({
        fieldId: field.value,
        inputType: 'abs', // Default input type
        value: undefined, // Clear the value
      })),
    [crossElastFields],
  );
  const onRowSelectionChange = useCallback((rows: any[]) => {
    if (setPggDt) {
      const values = rows.map((row: any) => ({
        value: row.id,
        label: row.name,
      }));
      setPggDt({ id: FILTER_NAMES.PROD, values, matchStrategy });
    }
  }, []);

  // Handler for editing individual rows
  const handleEditRow = useCallback((rowData?: any) => {
    if (rowData && rowData.id) {
      setSelectedRowForEdit(rowData.id);
      setSelectedRowData({
        category_name: rowData.category_name,
        name: rowData.name,
      });
    }
    setShowEditModal(true);
  }, []);

  // Add callback for when save is complete
  const handleEditSaveComplete = useCallback(() => {
    // Refresh both tables after save
    handleRunUpdates();
  }, [handleRunUpdates]);

  // Handler for Reset All functionality
  const handleResetAll = useCallback(async () => {
    if (!editBlocks || editBlocks.length === 0) {
      return;
    }
    if (!crossElastBlocks || crossElastBlocks.length === 0) {
      return;
    }
    setIsResettingAll(true);
    // Pass empty array for applyToFilterSelection to clear all data across all columns
    const filterSelection: FilterSelectedValue[] = [];

    // Reset Cross Elasticities
    await postScenarioPlannerInput<any>(
      id,
      crossElastBlocks,
      filterSelection,
      true,
      'cross',
    );
    // Reset inputs, main elasticity, and starting metrics
    await postScenarioPlannerInput<any>(id, editBlocks, filterSelection, true);
    setIsMassEditOpen(false);
    handleRunUpdates();
    setIsResettingAll(false);
  }, [id, crossElastBlocks, editBlocks, handleRunUpdates]);

  const handleDuplicate = () => {
    // Implement logic here
  };

  const handleExport = () => {
    // Implement logic here
  };

  const handleShare = () => {
    // Implement logic here
  };

  const filters = useAppFindFilters(useFilterContext(), [
    FILTER_NAMES.BRAND,
    FILTER_NAMES.SUBBRAND,
    FILTER_NAMES.SIZEGROUP,
    FILTER_NAMES.PPG,
  ]);
  const filterDict = useAppFilterDict(filters);
  const onSelectHandler = useCallback(
    (selectedValue: FilterSelectedValue) => {
      setFilterSelectionForMassEdit((value) => {
        return value
          ?.filter((i) => i.id !== selectedValue.id)
          .concat(selectedValue)
          .map((i) => ({
            ...i,
            matchStrategy: filterDict[i.id].defaultSelectedValue?.matchStrategy,
          }));
      });
    },
    [filterDict],
  );

  // Updates URL with encoded filters.
  useEncodeFilters(filterSelectionForMassEdit, false);

  const { show: isFullScreenMode, setShow: setIsFullScreenMode } =
    useSwitchPanel('fscreen', false);

  //const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'scenario' | 'summary'>(
    'scenario',
  );

  const [selectedRowData, setSelectedRowData] = useState<{
    category_name?: string;
    name?: string;
  }>();

  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreenMode(!isFullScreenMode);
    // Reset to scenario tab when entering full screen mode
    if (!isFullScreenMode) {
      setActiveTab('scenario');
    }
  }, [isFullScreenMode, setIsFullScreenMode]);

  const handleTabChange = (tab: 'scenario' | 'summary') => {
    setActiveTab(tab);
  };
  // Calculate table heights based on mode
  const scenarioTableHeight = useMemo(() => {
    if (isFullScreenMode) {
      return 'calc(100vh - 158px)';
    } else {
      return 500;
    }
  }, [isFullScreenMode]);

  const scenarioSummaryHeight = useMemo(() => {
    if (isFullScreenMode) {
      return 'calc(100vh - 235px)';
    } else {
      return 500;
    }
  }, [isFullScreenMode]);

  // Column selector popover content
  const columnSelectorPopover = (
    <Popover id="column-selector-popover" className="column-selector-popover">
      <Popover.Body>
        <div className="column-selector-content">
          {availableColumns.length > 0 && (
            <div className="column-selector-header">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="select-all-columns"
                  checked={visibleColumns.length === availableColumns.length}
                  onChange={(e) => handleSelectAllColumns(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="select-all-columns"
                >
                  Select All
                </label>
              </div>
            </div>
          )}
          <div className="column-list">
            {availableColumns.map((column) => (
              <div key={column.id} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`column-${column.id}`}
                  checked={visibleColumns.includes(column.id)}
                  onChange={() => handleColumnVisibilityToggle(column.id)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`column-${column.id}`}
                >
                  {column.name || column.id}
                </label>
              </div>
            ))}
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      {/* Main layout component handles all rendering logic based on isFullScreenMode */}
      <ScenarioLayoutManagerComponent
        isFullScreenMode={isFullScreenMode}
        // Breadcrumbs and copy plan button
        scenarioHeaderSection={
          <ScenarioHeaderComponent
            filterSelection={filterSelectionForExport}
            onDuplicate={handleDuplicate}
            onExport={handleExport}
            onShare={handleShare}
            isViewOnly={scenarioPlanner?.isOwner === false}
            scenarioPlanner={scenarioPlanner}
          />
        }
        // Scenario description
        titleSection={
          <div className="secnario-massedit-title-container">
            <div className="report-title">
              {scenarioPlanner?.name} | {periodText}, {timeframeName}
            </div>
            <div>
              <OverlayTrigger
                trigger="click"
                placement="bottom"
                rootClose
                overlay={
                  <Popover
                    id="scenario-details-popover"
                    className="scenario-details-popover"
                  >
                    <Popover.Body>
                      <div className="scenario-overlay-box">
                        <div className="scenario-title">
                          {scenarioPlanner?.name || 'Scenario Name'}
                        </div>
                        <p className="scenario-description">
                          {scenarioPlanner?.description ||
                            'Scenario Description'}
                        </p>

                        <div className="scenario-meta">
                          <div className="meta-row">
                            <div className="meta-label">Market</div>
                            <div className="meta-value">{marketText}</div>
                          </div>
                          <div className="meta-row">
                            <div className="meta-label">Category</div>
                            <div className="meta-value">{categoryText}</div>
                          </div>
                          <div className="meta-row">
                            <div className="meta-label">Date Range</div>
                            <div className="meta-value">{periodText}</div>
                          </div>
                        </div>
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <img
                  src={IMAGES_REPO.FileIcon}
                  alt="Info"
                  style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                />
              </OverlayTrigger>
            </div>
          </div>
        }
        // Only available in Full Screen mode.
        tabsSection={
          <div className="fullscreen-tabs-inline">
            <Nav
              variant="tabs"
              activeKey={activeTab}
              onSelect={(selectedKey) =>
                handleTabChange(selectedKey as 'scenario' | 'summary')
              }
            >
              <Nav.Item>
                <Nav.Link eventKey="scenario">Scenario</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="summary">Summary</Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
        }
        // Brand, Sub-Brand, Size Group, Pack, filters
        filterSection={
          <FilterPaneHeaderStandAloneComponent
            filterSelection={filterSelection}
            variant="stand-alone"
            filters={filters}
            defaultLabel="All"
            onSelect={onSelectHandler}
          />
        }
        // Full-Screen, Reset All, Mass Edit Buttons
        actionButtonsSection={
          <div className="breadcrumbs-right">
            <div className="d-flex justify-content-start gap-3">
              <Button
                variant="outline-secondary"
                className="btn-fullscreen-toggle"
                onClick={handleToggleFullScreen}
                title="Toggle Full Screen"
              >
                {isFullScreenMode ? (
                  <img src={IMAGES_REPO.ScenarioMinimize} alt="" />
                ) : (
                  <img src={IMAGES_REPO.ScenarioExpand} alt="" />
                )}
                <span style={{ color: '#2e90fa' }}>
                  {isFullScreenMode ? 'Minimize' : 'View Fullscreen'}
                </span>
              </Button>
              <Button
                variant="outline-secondary"
                className="btn-common"
                onClick={handleResetAll}
                disabled={!scenarioPlanner?.isOwner || isResettingAll}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 4V10H7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.51 15A9 9 0 1 0 6 5L1 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isResettingAll ? 'Resetting...' : 'Reset All'}
              </Button>
              <Button
                variant="default"
                className="btn-default-blue"
                onClick={handleMassEditOpen}
                disabled={!scenarioPlanner?.isOwner}
              >
                Mass Edit
              </Button>
            </div>
          </div>
        }
        // Main input table
        scenarioTableContent={
          <BodySectionComponent>
            <div className="container-fluid report-container scenario-top-table-container">
              <div className="row">
                <div className="col-12 report-title-main">
                  <AppErrorBoundaryComponent>
                    <ScenarioPlannerDetailsTableComponent
                      reportName={REPORT_NAMES.SCENARIO_PLANNER_DATA}
                      totalReportName={REPORT_NAMES.SCENARIO_PLANNER_DATA_TOTAL}
                      reportId={id}
                      filterSelection={filterSelection}
                      setInvalidateQuery={setDataTableRefresh}
                      onRowSelectionChange={onRowSelectionChange}
                      idField="id"
                      inputTypeOptions={inputTypeOptions}
                      onRunEdit={handleTableRunEdit}
                      onRunEditMode={handleRunChangeMode}
                      height={scenarioTableHeight}
                      showEditIconOnHover={true}
                      onRowEditClick={handleEditRow}
                    />
                  </AppErrorBoundaryComponent>
                </div>
              </div>
            </div>
          </BodySectionComponent>
        }
        // Summary reports
        summaryTableContent={
          <BodySectionComponent>
            <div className="container-fluid report-container summary-report-container">
              <div className="row">
                <div className="col-2 report-title-main">
                  <div className="report-title-container">
                    <div className="report-title">Summary</div>
                  </div>
                </div>
                <div className="col-6 summary-product-selection">
                  <div className="report-title-container">
                    <FilterTabButtonStandAloneComponent
                      onSelect={(val) => (val ? setProdSelHier(val) : null)}
                      filter={{
                        id: FILTER_NAMES.SCENARIOPLANNERPRODHIER,
                        name: 'Product Level',
                        storeKey: '',
                        required: true,
                        order: 1,
                        type: FILTER_TYPES.SINGLE,
                        defaultSelectedValue: defaultProdHier,
                        selectedValue: defaultProdHier,
                      }}
                      filterSelection={filterSelectionForSummaryTabs}
                      disabled={false}
                    />
                  </div>
                </div>
                <div className="col-4 summary-actions d-flex align-items-center justify-content-end gap-3">
                  <div className="d-flex justify-content-end gap-3">
                    <RowActionDropdown
                      selectedRows={selectedRowsForHiding}
                      onHideSelected={handleHideSelected}
                      onSelectRowsToHide={handleSelectRowsToHide}
                      onShowSelected={() => {
                        handleShowSelectedInternal(selectedRowsForHiding);
                      }}
                      onResetRows={handleResetRows}
                      hasSelection={selectedRowsForHiding.length > 0}
                      hasHiddenRows={getCurrentHiddenRows().size > 0}
                      // NEW: Pass show selected state
                      isShowSelectedMode={showSelectedMode.length > 0}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom-end"
                      rootClose
                      show={showColumnSelector}
                      onToggle={(show) => setShowColumnSelector(show)}
                      overlay={columnSelectorPopover}
                    >
                      <Button
                        variant="outline-secondary"
                        className="btn-common btn btn-outline-secondary"
                        disabled={availableColumns.length === 0}
                        size="sm"
                      >
                        <img
                          src={IMAGES_REPO.FilterLines}
                          alt="Edit Columns"
                          className="icon-edit-columns"
                        />
                        Edit columns
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className="col-12 summary-table-content">
                  <AppErrorBoundaryComponent>
                    <ScenarioPlannerSummaryTableComponent
                      scenarioSummarySelected={scenarioSumarySelected}
                      reportName={REPORT_NAMES.SCENARIO_PLANNER_CALCS}
                      totalReportName={
                        REPORT_NAMES.SCENARIO_PLANNER_CALCS_TOTAL
                      }
                      reportId={id}
                      filterSelection={filterSelectionForSummary}
                      setInvalidateQuery={setCalcsTableRefresh}
                      height={scenarioSummaryHeight}
                      onColumnsUpdate={handleColumnsUpdate}
                      visibleColumns={visibleColumns}
                      // Props for row hiding functionality - with proper values
                      hiddenRows={getCurrentHiddenRows()}
                      selectedRowsForHiding={selectedRowsForHiding}
                      onRowSelectionForHiding={handleRowSelectionForHiding}
                      showOnlyFilteredRows={showSelectedMode}
                      onClearShowOnlyFiltered={() => setShowSelectedMode([])}
                      // Pass show-only mode state to disable selection
                      isShowOnlyFilteredMode={showSelectedMode.length > 0}
                      // Ensure row selection is enabled with correct field
                      enableRowSelection={true}
                      enableMultiRowSelection={true}
                      idField="id"
                    />
                  </AppErrorBoundaryComponent>
                </div>
              </div>
            </div>
          </BodySectionComponent>
        }
        activeTab={activeTab}
      />

      {/** End of layout */}

      {defaultEditBlocks && (
        <MassEditSidebarComponent
          isOpen={isMassEditOpen}
          onClose={handleMassEditClose}
          onRunEdit={handleRunMassEdit}
          fieldOptions={inputFields}
          defaultEditBlocks={defaultEditBlocks}
          defaultApplyMode={defaultApplyMode}
          scenarioPlannerFilterSel={scenarioPlannerFilterSel}
          selectedRows={ppgSelValue}
        />
      )}
      <CopyScenarioModal
        show={showCopyModal}
        setShow={setShowCopyModal}
        onCopy={() => {}}
      />
      {selectedRowForEdit && (
        <ScenarioPlannerEditModalComponent
          show={showEditModal}
          onClose={(show: boolean) => {
            setShowEditModal(show);
            if (!show) {
              setSelectedRowForEdit(null);
              setSelectedRowData(undefined);
            }
          }}
          rowId={selectedRowForEdit}
          scenarioPlannerId={id}
          onSaveComplete={handleEditSaveComplete}
          rowData={selectedRowData}
          mainElastBlocks={mainElastBlocks}
          crossElastBlocks={crossElastBlocks}
          startingMetricsBlocks={startingMetricsBlocks}
          isViewOnly={scenarioPlanner?.isOwner === false}
        />
      )}
    </>
  );
};

export default ScenarioPlannerMassEditComponent;
